/**
 * EventsPicker — single OR multi-event chooser for tile viz_config.
 *   • Click an event in the dropdown → added as a chip below.
 *   • Click the X on a chip → removed.
 *   • Saves to backend as:
 *       { event: 'x' }              when exactly one is picked
 *       { events: ['x','y','z'] }   when multiple
 *   • Used for big_number, time_series, funnel — anywhere "any of these events" matters.
 */
import React, { useMemo } from 'react';
import { Box, Select, Tag, TagLabel, TagCloseButton, Text, Wrap, WrapItem } from '@chakra-ui/react';

export default function EventsPicker({ value, onChange, events = [], loading = false }) {
  // Normalize "value" to always be an array internally.
  const selected = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value) return [value];
    return [];
  }, [value]);

  // Save back in the right shape for the backend.
  const emit = (next) => {
    if (!next || next.length === 0) onChange({ event: '', events: undefined });
    else if (next.length === 1)     onChange({ event: next[0], events: undefined });
    else                            onChange({ event: undefined, events: next });
  };

  const add = (eventName) => {
    if (!eventName || selected.includes(eventName)) return;
    emit([...selected, eventName]);
  };
  const remove = (eventName) => emit(selected.filter((e) => e !== eventName));

  const available = events.filter((ev) => !selected.includes(ev.name));

  return (
    <Box>
      {/* Selected chips */}
      {selected.length > 0 && (
        <Wrap spacing={2} mb={2}>
          {selected.map((name) => {
            const meta = events.find((e) => e.name === name);
            return (
              <WrapItem key={name}>
                <Tag size="md" colorScheme="blue" borderRadius="full">
                  <TagLabel>{meta?.display_name || name}</TagLabel>
                  <TagCloseButton onClick={() => remove(name)} />
                </Tag>
              </WrapItem>
            );
          })}
        </Wrap>
      )}

      {/* Add picker */}
      <Select
        value=""
        onChange={(e) => add(e.target.value)}
        placeholder={loading ? 'Loading events…' : (selected.length === 0 ? 'Pick an event' : '+ add another event (OR)')}
        isDisabled={loading || available.length === 0}
      >
        {available.map((ev) => (
          <option key={ev.name} value={ev.name}>{ev.display_name || ev.name}</option>
        ))}
      </Select>

      {selected.length === 0 && (
        <Text fontSize="xs" color="gray.500" mt={1}>
          Pick one event — or pick multiple to count customers who did <strong>any of</strong> them
          (e.g. for "Active Customers" pick all transaction + login events).
        </Text>
      )}
      {selected.length > 1 && (
        <Text fontSize="xs" color="blue.600" mt={1}>
          ✓ This tile will match customers who did <strong>any of</strong> {selected.length} events.
        </Text>
      )}
    </Box>
  );
}
